import { useCallback, useEffect, useRef, useState } from 'react';
import { playArcadeSound, unlockArcadeAudio } from '../../shared/audio/audio';
import {
  COARSE_POINTER_QUERY,
  canUseTouchControls,
} from '../../app/startOptions';
import { trackEvent } from '../../app/telemetry';
import { useKeyboardControls } from '../../shared/controls/useKeyboardControls';
import PhaserGame from '../../shared/phaser/PhaserGame';
import {
  readMaxScore,
  readRecords,
  writeMaxScore,
  writeRecords,
} from '../../shared/storage/localRecords';
import { createNeonRelayGameConfig } from './createNeonRelayGameConfig';
import {
  calculateRouteGrade,
  createInitialRelayState,
} from './neonRelayLogic';
import { neonRelayDefinition } from './neonRelayDefinition';
import './NeonRelayGame.css';

const EMPTY_CONTROLLER_INPUT = {
  moveX: 0,
  moveY: 0,
  primary: false,
  secondary: false,
  confirm: false,
  cancel: false,
};

const ROUTE_ADVANCE_DELAY_MS = 900;
const SWIPE_LANE_DISTANCE_PX = 34;
const SWIPE_INPUT_PULSE_MS = 90;

export function getSwipeLaneDirection(startX, currentX, threshold = SWIPE_LANE_DISTANCE_PX) {
  const deltaX = currentX - startX;

  if (Math.abs(deltaX) < threshold) {
    return 0;
  }

  return deltaX > 0 ? 1 : -1;
}

function formatStatus(status, won) {
  if (won) {
    return 'Won';
  }

  if (status === 'routeComplete') {
    return 'Clear';
  }

  if (status === 'gameOver') {
    return 'Lost';
  }

  return 'Active';
}

function NeonRelaySwipeControls({ onLanePulse }) {
  const pointerRef = useRef(null);
  const resetTimerRef = useRef(null);

  const clearPulseTimer = useCallback(() => {
    if (resetTimerRef.current) {
      window.clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }
  }, []);

  const resetPulse = useCallback(() => {
    clearPulseTimer();
    onLanePulse(0);
  }, [clearPulseTimer, onLanePulse]);

  const pulseLane = useCallback((direction) => {
    clearPulseTimer();
    onLanePulse(direction);
    resetTimerRef.current = window.setTimeout(() => {
      onLanePulse(0);
      resetTimerRef.current = null;
    }, SWIPE_INPUT_PULSE_MS);
  }, [clearPulseTimer, onLanePulse]);

  useEffect(() => () => {
    clearPulseTimer();
    onLanePulse(0);
  }, [clearPulseTimer, onLanePulse]);

  return (
    <div
      aria-label="Switch lanes"
      aria-valuemax="1"
      aria-valuemin="-1"
      aria-valuenow="0"
      className="neon-relay-swipe-control"
      role="slider"
      tabIndex="0"
      onPointerDown={(event) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        pointerRef.current = {
          id: event.pointerId,
          x: event.clientX,
        };
        resetPulse();
      }}
      onPointerMove={(event) => {
        const activePointer = pointerRef.current;

        if (!activePointer || activePointer.id !== event.pointerId) {
          return;
        }

        const direction = getSwipeLaneDirection(activePointer.x, event.clientX);

        if (direction === 0) {
          return;
        }

        pointerRef.current = {
          ...activePointer,
          x: event.clientX,
        };
        pulseLane(direction);
      }}
      onPointerUp={(event) => {
        if (pointerRef.current?.id === event.pointerId) {
          pointerRef.current = null;
          resetPulse();
        }
      }}
      onPointerCancel={(event) => {
        if (pointerRef.current?.id === event.pointerId) {
          pointerRef.current = null;
          resetPulse();
        }
      }}
    >
      <span className="neon-relay-swipe-rail" aria-hidden="true">
        <span className="neon-relay-swipe-chevron neon-relay-swipe-chevron-left" />
        <span className="neon-relay-swipe-thumb" />
        <span className="neon-relay-swipe-chevron neon-relay-swipe-chevron-right" />
      </span>
    </div>
  );
}

function NeonRelayGame({
  definition = neonRelayDefinition,
  onReturnToLibrary,
  selectedPlayableIcon = 'neon-relay',
}) {
  const [routeIndex, setRouteIndex] = useState(0);
  const [instanceKey, setInstanceKey] = useState(0);
  const [carriedScore, setCarriedScore] = useState(0);
  const [relay, setRelay] = useState(() => ({
    ...createInitialRelayState(0, definition),
    status: 'playing',
  }));
  const [maxScore, setMaxScore] = useState(() =>
    readMaxScore(window.localStorage, definition.storageNamespace)
  );
  const [, setRecords] = useState(() =>
    readRecords(window.localStorage, definition.storageNamespace)
  );
  const [showTouchControls, setShowTouchControls] = useState(canUseTouchControls);
  const [reducedMotion, setReducedMotion] = useState(false);
  const controllerInputRef = useRef(EMPTY_CONTROLLER_INPUT);
  const relayRef = useRef(relay);
  const lastRouteStartedKeyRef = useRef(null);

  useEffect(() => {
    relayRef.current = relay;
  }, [relay]);

  useEffect(() => {
    const routeStartedKey = `${relay.routeIndex}:${instanceKey}`;

    if (relay.status !== 'playing' || lastRouteStartedKeyRef.current === routeStartedKey) {
      return;
    }

    lastRouteStartedKeyRef.current = routeStartedKey;
    trackEvent('neon_relay.route_started', {
      gameId: definition.id,
      routeIndex: relay.routeIndex,
      routeName: relay.routeName,
      selectedPlayableIcon,
    });
  }, [definition.id, instanceKey, relay.routeIndex, relay.routeName, relay.status, selectedPlayableIcon]);

  const resetControllerInput = useCallback(() => {
    controllerInputRef.current = EMPTY_CONTROLLER_INPUT;
  }, []);

  const startRoute = useCallback((nextRouteIndex = 0, nextCarriedScore = 0) => {
    unlockArcadeAudio();
    resetControllerInput();
    setRouteIndex(nextRouteIndex);
    setCarriedScore(nextCarriedScore);
    setRelay({
      ...createInitialRelayState(nextRouteIndex, definition),
      status: 'playing',
      score: nextCarriedScore,
    });
    setInstanceKey((current) => current + 1);
  }, [definition, resetControllerInput]);

  const restartRun = useCallback(() => {
    startRoute(0, 0);
  }, [startRoute]);

  const startNextRoute = useCallback(() => {
    const current = relayRef.current;
    const nextRouteIndex = Math.min(current.routeIndex + 1, definition.routes.length - 1);

    startRoute(nextRouteIndex, current.score);
  }, [definition.routes.length, startRoute]);

  useEffect(() => {
    const mediaQuery = window.matchMedia?.(COARSE_POINTER_QUERY);

    if (!mediaQuery) {
      return undefined;
    }

    const updateTouchControls = () => {
      setShowTouchControls(canUseTouchControls());
    };

    mediaQuery.addEventListener?.('change', updateTouchControls);
    mediaQuery.addListener?.(updateTouchControls);

    return () => {
      mediaQuery.removeEventListener?.('change', updateTouchControls);
      mediaQuery.removeListener?.(updateTouchControls);
    };
  }, []);

  const onInputChange = useCallback((input) => {
    controllerInputRef.current = input;
  }, []);

  const onSwipeLanePulse = useCallback((direction) => {
    controllerInputRef.current = {
      ...EMPTY_CONTROLLER_INPUT,
      moveX: direction,
    };
  }, []);

  const confirmControllerInput = useCallback(() => {
    const current = relayRef.current;

    if (current.status === 'routeComplete') {
      startNextRoute();
      return;
    }

    if (current.status === 'gameOver') {
      restartRun();
    }
  }, [restartRun, startNextRoute]);

  useKeyboardControls({
    onConfirm: confirmControllerInput,
    onInputChange,
  });

  const handleStateChange = useCallback((nextRelay) => {
    setRelay(nextRelay);
  }, []);

  const saveScoreIfNeeded = useCallback((score) => {
    setMaxScore((currentBest) => {
      if (score <= currentBest) {
        return currentBest;
      }

      writeMaxScore(score, window.localStorage, definition.storageNamespace);
      return score;
    });
  }, [definition.storageNamespace]);

  const updateRouteRecords = useCallback((nextRelay) => {
    setRecords((records) => {
      const routeKey = String(nextRelay.routeIndex + 1);
      const completedSectors = records.normal.completedSectors ?? [];
      const nextRecords = {
        ...records,
        normal: {
          ...records.normal,
          completedSectors: completedSectors.includes(nextRelay.routeIndex)
            ? completedSectors
            : [...completedSectors, nextRelay.routeIndex],
          badges: {
            ...(records.normal.badges ?? {}),
            [`route-${routeKey}-streak-${nextRelay.bestStreak}`]: true,
            [`route-${routeKey}-grade-${calculateRouteGrade(nextRelay, definition.routes[nextRelay.routeIndex])}`]: true,
          },
        },
      };

      nextRecords.normal.bestScore = Math.max(nextRecords.normal.bestScore ?? 0, nextRelay.score);
      nextRecords.normal.bestStreak = Math.max(nextRecords.normal.bestStreak ?? 0, nextRelay.bestStreak);
      writeRecords(nextRecords, window.localStorage, definition.storageNamespace);
      return nextRecords;
    });
  }, [definition.routes, definition.storageNamespace]);

  const handleRouteComplete = useCallback((nextRelay) => {
    resetControllerInput();
    playArcadeSound('success');
    trackEvent('neon_relay.route_completed', {
      gameId: definition.id,
      packets: nextRelay.packets,
      routeIndex: nextRelay.routeIndex,
      routeName: nextRelay.routeName,
      score: nextRelay.score,
    });
    if (nextRelay.routeIndex >= definition.routes.length - 1) {
      trackEvent('neon_relay.won', {
        gameId: definition.id,
        routeIndex: nextRelay.routeIndex,
        score: nextRelay.score,
      });
    }
    setRelay(nextRelay);
    saveScoreIfNeeded(nextRelay.score);
    updateRouteRecords(nextRelay);
  }, [definition.id, definition.routes.length, resetControllerInput, saveScoreIfNeeded, updateRouteRecords]);

  const handleGameOver = useCallback((nextRelay) => {
    resetControllerInput();
    playArcadeSound('damage');
    trackEvent('neon_relay.signal_lost', {
      gameId: definition.id,
      packets: nextRelay.packets,
      routeIndex: nextRelay.routeIndex,
      routeName: nextRelay.routeName,
      score: nextRelay.score,
    });
    setRelay(nextRelay);
    saveScoreIfNeeded(nextRelay.score);
  }, [definition.id, resetControllerInput, saveScoreIfNeeded]);

  const createConfig = useCallback(({ Phaser }) => createNeonRelayGameConfig({
    Phaser,
    controllerInputRef,
    definition,
    events: {
      onDamage: () => playArcadeSound('damage'),
      onGameOver: handleGameOver,
      onLaneChange: () => playArcadeSound('laser'),
      onPacket: () => playArcadeSound('powerup'),
      onRouteComplete: handleRouteComplete,
      onScore: () => {},
      onStateChange: handleStateChange,
    },
    reducedMotion,
    routeIndex,
    score: carriedScore,
    selectedPlayableIcon,
  }), [
    carriedScore,
    definition,
    handleGameOver,
    handleRouteComplete,
    handleStateChange,
    reducedMotion,
    routeIndex,
    selectedPlayableIcon,
  ]);

  const route = definition.routes[relay.routeIndex] ?? definition.routes[routeIndex];
  const won = relay.status === 'routeComplete' && relay.routeIndex >= definition.routes.length - 1;
  const isPlaying = relay.status === 'playing';
  const isNextRouteDialog = relay.status === 'routeComplete' && !won;
  const isSignalLostDialog = relay.status === 'gameOver';
  const isWinDialog = won;
  const grade = calculateRouteGrade(relay, route);
  const routeProgress = Math.min(100, (relay.packets / route.targetPackets) * 100);

  useEffect(() => {
    if (relay.status !== 'routeComplete' || won) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      startNextRoute();
    }, ROUTE_ADVANCE_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [relay.status, startNextRoute, won]);

  return (
    <main className="game-app neon-relay-app">
      <section className="game-shell neon-relay-shell" aria-labelledby="app-title">
        <header className="hud neon-relay-hud">
          <div className="hud-title">
            <h1 id="app-title">{definition.name}</h1>
            <p>{relay.routeName}. {definition.instructions}</p>
          </div>
          <div className="hud-stats" aria-live="polite">
            <div className="score-panel">
              <span>Route</span>
              <strong>{relay.routeIndex + 1}/{definition.routes.length}</strong>
            </div>
            <div className="score-panel">
              <span>Status</span>
              <strong>{formatStatus(relay.status, won)}</strong>
            </div>
            <div className="score-panel">
              <span>Time</span>
              <strong>{Math.ceil(relay.timeRemainingMs / 1000)}s</strong>
            </div>
            <div className="score-panel">
              <span>Core</span>
              <strong>{relay.integrity}</strong>
            </div>
            <div className="score-panel">
              <span>Score</span>
              <strong>{relay.score}</strong>
            </div>
            <div className="score-panel">
              <span>Best</span>
              <strong>{maxScore}</strong>
            </div>
            <div className="score-panel">
              <span>Grade</span>
              <strong>{grade}</strong>
            </div>
          </div>
        </header>

        <div className="relay-field">
          <PhaserGame
            key={`${selectedPlayableIcon}-${routeIndex}-${instanceKey}-${reducedMotion ? 'reduced' : 'motion'}`}
            ariaLabel="Neon Relay playfield"
            className="neon-relay-phaser-field"
            createConfig={createConfig}
          />

          <div className="relay-progress" aria-label="Packet delivery">
            <div className="relay-progress-copy">
              <strong>Packets</strong>
              <span>{relay.packets}/{route.targetPackets}</span>
            </div>
            <div className="relay-progress-track" aria-hidden="true">
              <span style={{ width: `${routeProgress}%` }} />
            </div>
          </div>

          {!isPlaying && (
            <div className={`game-overlay game-overlay-${won ? 'won' : relay.status}`} role="dialog" aria-live="polite" aria-label="Neon Relay route summary">
              <div className={[
                'overlay-panel',
                `overlay-panel-${won ? 'won' : relay.status}`,
                isNextRouteDialog ? 'neon-relay-next-panel' : '',
                isSignalLostDialog ? 'neon-relay-lost-panel' : '',
                isWinDialog ? 'neon-relay-win-panel' : '',
              ].filter(Boolean).join(' ')}
              >
                <p>
                  {won
                    ? 'Delivery Complete'
                    : relay.status === 'routeComplete'
                      ? 'Route Clear'
                      : 'Signal Lost'}
                </p>
                <strong>
                  {won
                    ? 'Network saved'
                    : relay.status === 'routeComplete'
                      ? 'Uplink stable'
                      : 'Run ended'}
                </strong>
                {isNextRouteDialog ? (
                  <>
                    <div className="neon-relay-next-stats" aria-label="Route clear summary">
                      <span>
                        Grade <strong>{grade}</strong>
                      </span>
                      <span>
                        Streak <strong>{relay.bestStreak}</strong>
                      </span>
                    </div>
                    <button type="button" onClick={startNextRoute}>
                      Next Route
                    </button>
                  </>
                ) : isSignalLostDialog ? (
                  <>
                    <div className="neon-relay-lost-score" aria-label="Final score">
                      <span>Score</span>
                      <strong>{relay.score}</strong>
                    </div>
                    <div className="neon-relay-lost-stats" aria-label="Signal lost summary">
                      <span>
                        Packets <strong>{relay.packets}/{route.targetPackets}</strong>
                      </span>
                      <span>
                        Streak <strong>{relay.bestStreak}</strong>
                      </span>
                    </div>
                    <div className="neon-relay-lost-actions">
                      <button type="button" onClick={restartRun}>
                        Restart
                      </button>
                      {onReturnToLibrary && (
                        <button className="secondary-action" type="button" onClick={onReturnToLibrary}>
                          Game Menu
                        </button>
                      )}
                    </div>
                  </>
                ) : isWinDialog ? (
                  <>
                    <div className="neon-relay-win-score" aria-label="Final score">
                      <span>Score</span>
                      <strong>{relay.score}</strong>
                    </div>
                    <div className="neon-relay-win-stats" aria-label="Delivery complete summary">
                      <span>
                        Grade <strong>{grade}</strong>
                      </span>
                      <span>
                        Best <strong>{maxScore}</strong>
                      </span>
                    </div>
                    <div className="neon-relay-win-actions">
                      <button type="button" onClick={restartRun}>
                        Restart
                      </button>
                      {onReturnToLibrary && (
                        <button className="secondary-action" type="button" onClick={onReturnToLibrary}>
                          Game Menu
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <dl className="summary-grid">
                      <div>
                        <dt>Packets</dt>
                        <dd>{relay.packets}/{route.targetPackets}</dd>
                      </div>
                      <div>
                        <dt>Streak</dt>
                        <dd>{relay.bestStreak}</dd>
                      </div>
                      <div>
                        <dt>Grade</dt>
                        <dd>{grade}</dd>
                      </div>
                      <div>
                        <dt>Best</dt>
                        <dd>{maxScore}</dd>
                      </div>
                    </dl>
                    <button type="button" onClick={restartRun}>
                      Restart
                    </button>
                  </>
                )}
                {!isNextRouteDialog && !isSignalLostDialog && !isWinDialog ? (
                  <label className="neon-relay-toggle">
                    <input
                      type="checkbox"
                      checked={reducedMotion}
                      onChange={(event) => setReducedMotion(event.target.checked)}
                    />
                    Reduced motion
                  </label>
                ) : null}
                {onReturnToLibrary && !isNextRouteDialog && !isSignalLostDialog && !isWinDialog && (
                  <button className="secondary-action" type="button" onClick={onReturnToLibrary}>
                    Game Menu
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {showTouchControls && isPlaying && (
          <NeonRelaySwipeControls onLanePulse={onSwipeLanePulse} />
        )}
      </section>
    </main>
  );
}

export default NeonRelayGame;
